import type { 
  NameType,
  NameSubcategory,
  PersonSubcategory,
  WeaponSubcategory,
  SkillSubcategory,
  MagicSubcategory,
  PlaceSubcategory,
  OrganizationSubcategory,
  ItemSubcategory,
  EnemySubcategory,
} from '@creanizer/types'
import type { CharacterInput, CharacterResult, SavedCharacter } from '../../../../types/character' // temp fix
import type { CustomValue } from '@creanizer/utils'

interface SubcategorySelectorProps {
  nameType: NameType
  subcategory: NameSubcategory | undefined
  setSubcategory: (value: NameSubcategory | undefined) => void
  customSubcategory: string
  setCustomSubcategory: (value: string) => void
  savedSubcategories: CustomValue[]
  addCustomValue: (type: CustomValue['type'], value: string, label: string, generatorType?: CustomValue['generatorType'], nameType?: string) => Promise<void>
  useCustomValue: (id: string) => Promise<void>
}

export function SubcategorySelector({
  nameType,
  subcategory,
  setSubcategory,
  customSubcategory,
  setCustomSubcategory,
  savedSubcategories,
  addCustomValue,
  useCustomValue,
}: SubcategorySelectorProps) {
  const renderCustomInput = () => (
    <>
      {subcategory === 'custom' && (
        <div className="mt-2 space-y-2">
          <input
            type="text"
            className="input"
            placeholder="カスタムサブカテゴリを入力"
            value={customSubcategory}
            onChange={(e) => setCustomSubcategory(e.target.value)}
          />
          {customSubcategory && (
            <button
              type="button"
              onClick={async () => {
                await addCustomValue('subcategory', customSubcategory, customSubcategory, 'name', nameType)
                alert(`カスタムサブカテゴリ "${customSubcategory}" を保存しました`)
              }}
              className="btn btn-secondary text-sm"
            >
              💾 保存
            </button>
          )}
        </div>
      )}
      {savedSubcategories.length > 0 && (
        <div className="mt-2">
          <label className="text-xs text-gray-600">保存済みカスタム:</label>
          <div className="flex flex-wrap gap-1 mt-1">
            {savedSubcategories.map(cv => (
              <button
                key={cv.id}
                type="button"
                onClick={() => {
                  setSubcategory('custom')
                  setCustomSubcategory(cv.value)
                  useCustomValue(cv.id)
                }}
                className="px-2 py-1 text-xs bg-purple-100 hover:bg-purple-200 rounded"
              >
                {cv.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  )

  if (nameType.startsWith('person')) {
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          サブカテゴリ
        </label>
        <select
          className="input"
          value={subcategory || ''}
          onChange={(e) => setSubcategory(e.target.value as PersonSubcategory || undefined)}
        >
          <option value="">指定なし</option>
          <option value="male">男性</option>
          <option value="female">女性</option>
          <option value="neutral">中性的</option>
          <option value="codename">コードネーム</option>
          <option value="custom">カスタム...</option>
        </select>
        {renderCustomInput()}
      </div>
    )
  }

  if (nameType === 'weapon') {
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          サブカテゴリ
        </label>
        <select
          className="input"
          value={subcategory || ''}
          onChange={(e) => setSubcategory(e.target.value as WeaponSubcategory || undefined)}
        >
          <option value="">指定なし</option>
          <option value="sword">剣・刀剣</option>
          <option value="spear">槍・長柄武器</option>
          <option value="bow">弓・射撃武器</option>
          <option value="axe">斧・打撃武器</option>
          <option value="gun">銃・火器</option>
          <option value="magic_weapon">魔法武器</option>
          <option value="special">特殊武器</option>
          <option value="custom">カスタム...</option>
        </select>
        {renderCustomInput()}
      </div>
    )
  }

  if (nameType === 'skill') {
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          サブカテゴリ
        </label>
        <select
          className="input"
          value={subcategory || ''}
          onChange={(e) => setSubcategory(e.target.value as SkillSubcategory || undefined)}
        >
          <option value="">指定なし</option>
          <option value="attack">攻撃技</option>
          <option value="defense">防御技</option>
          <option value="support">補助技</option>
          <option value="healing">回復技</option>
          <option value="ultimate">必殺技</option>
          <option value="custom">カスタム...</option>
        </select>
        {renderCustomInput()}
      </div>
    )
  }

  if (nameType === 'magic') {
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          サブカテゴリ
        </label>
        <select
          className="input"
          value={subcategory || ''}
          onChange={(e) => setSubcategory(e.target.value as MagicSubcategory || undefined)}
        >
          <option value="">指定なし</option>
          <option value="attack">攻撃魔法</option>
          <option value="defense">防御魔法</option>
          <option value="support">補助魔法</option>
          <option value="healing">回復魔法</option>
          <option value="summon">召喚魔法</option>
          <option value="custom">カスタム...</option>
        </select>
        {renderCustomInput()}
      </div>
    )
  }

  if (nameType === 'place') {
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          サブカテゴリ
        </label>
        <select
          className="input"
          value={subcategory || ''}
          onChange={(e) => setSubcategory(e.target.value as PlaceSubcategory || undefined)}
        >
          <option value="">指定なし</option>
          <option value="city">都市</option>
          <option value="country">国家</option>
          <option value="continent">大陸</option>
          <option value="nature">自然地形</option>
          <option value="building">建造物</option>
          <option value="custom">カスタム...</option>
        </select>
        {renderCustomInput()}
      </div>
    )
  }

  if (nameType === 'organization') {
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          サブカテゴリ
        </label>
        <select
          className="input"
          value={subcategory || ''}
          onChange={(e) => setSubcategory(e.target.value as OrganizationSubcategory || undefined)}
        >
          <option value="">指定なし</option>
          <option value="company">企業・会社</option>
          <option value="guild">ギルド・組合</option>
          <option value="government">政府・公的機関</option>
          <option value="secret">秘密結社</option>
          <option value="custom">カスタム...</option>
        </select>
        {renderCustomInput()}
      </div>
    )
  }

  if (nameType === 'item') {
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          サブカテゴリ
        </label>
        <select
          className="input"
          value={subcategory || ''}
          onChange={(e) => setSubcategory(e.target.value as ItemSubcategory || undefined)}
        >
          <option value="">指定なし</option>
          <option value="consumable">消耗品</option>
          <option value="equipment">装備品</option>
          <option value="material">素材</option>
          <option value="key_item">キーアイテム</option>
          <option value="custom">カスタム...</option>
        </select>
        {renderCustomInput()}
      </div>
    )
  }

  if (nameType === 'enemy') {
    return (
      <div>
        <label className="block text-sm font-medium mb-2">
          サブカテゴリ
        </label>
        <select
          className="input"
          value={subcategory || ''}
          onChange={(e) => setSubcategory(e.target.value as EnemySubcategory || undefined)}
        >
          <option value="">指定なし</option>
          <option value="boss">ボス</option>
          <option value="miniboss">中ボス</option>
          <option value="mob">雑魚</option>
          <option value="humanoid">人型</option>
          <option value="beast">獣型</option>
          <option value="undead">不死型</option>
          <option value="machine">機械型</option>
          <option value="custom">カスタム...</option>
        </select>
        {renderCustomInput()}
      </div>
    )
  }

  // カスタム名前タイプの場合
  return (
    <div>
      <label className="block text-sm font-medium mb-2">
        サブカテゴリ
      </label>
      <select
        className="input"
        value={subcategory === 'custom' ? 'custom' : (subcategory || '')}
        onChange={(e) => {
          if (e.target.value === 'custom') {
            setSubcategory('custom')
            setCustomSubcategory('')
          } else {
            setSubcategory(e.target.value || undefined)
            setCustomSubcategory('')
          }
        }}
      >
        <option value="">指定なし</option>
        <option value="custom">カスタム...</option>
      </select>
      {renderCustomInput()}
    </div>
  )
}